import React, { createContext, useContext, useEffect, useState } from 'react'
import { getActionTypes, getCategories, getTriggers } from './alert-routing-api'

export const CatalogContext = createContext({ loaded: false, triggers: [], actionTypes: [], categories: [] })
export const useCatalog = () => useContext(CatalogContext)

export const CatalogProvider = ({ children }) => {
  const [c, setC] = useState({ loaded: false, triggers: [], actionTypes: [], categories: [] })
  useEffect(() => {
    Promise.all([getTriggers(), getActionTypes(), getCategories()]).then(
      ([triggers, actionTypes, categories]) => setC({ loaded: true, triggers, actionTypes, categories }),
      err => setC(prev => ({ ...prev, loaded: true, error: err })))
  }, [])
  return <CatalogContext.Provider value={c}>{children}</CatalogContext.Provider>
}

// An organization's route (scopeType organization, or an organization id),
// as core's Route.organizationScoped reads it; else a platform route.
export const routeOrgScoped = route =>
  Boolean(route) && (route.scopeType === 'organization' || (route.organizationID !== null && route.organizationID !== undefined))

// core's Route.Covers: a route covers the triggers it names, or -- naming
// none -- every trigger of its category. A platform-only trigger
// (platformOnly: sso.staff_link, SSO link confirmation review L7) is
// covered only by a platform route that names it: never by an
// organization's route, never by a category-wide one.
export const coversTrigger = (route, t) => {
  if (t.platformOnly && routeOrgScoped(route)) return false
  const kinds = route.triggerKinds || []
  if (kinds.length === 0) return t.category === route.category && !t.platformOnly
  return kinds.includes(t.kind)
}

// The triggers of the route's category it covers today.
export const coveredTriggers = (route, catalog) =>
  catalog.triggers.filter(t => t.category === route.category && coversTrigger(route, t))

// The triggers of the route's category it may name: an organization's
// route never names a platform-only one (core refuses it:
// alert_route.invalid_trigger), so its editor never offers one.
export const nameableTriggers = (route, catalog) =>
  catalog.triggers.filter(t => t.category === route.category && !(t.platformOnly && routeOrgScoped(route)))

// The fields a route may filter on: the common filterable fields of the
// chosen triggers, or the category's for a wildcard (spec §11.1, R16).
export const filterFields = (route, catalog) => {
  const cat = catalog.categories.find(c => c.key === route.category)
  if (!route.triggerKinds || route.triggerKinds.length === 0) return (cat && cat.commonFields) || []
  const chosen = catalog.triggers.filter(t => route.triggerKinds.includes(t.kind))
  if (chosen.length === 0) return []
  return chosen[0].schema.filter(f => f.filterable && chosen.every(t => t.schema.some(g => g.key === f.key && g.type === f.type && g.filterable)))
}
